// src/hooks/useTagManagement.ts
import { useMemo, useState } from "react";
//-tech-debt-react-query;
import { useQueryClient } from '@tanstack/react-query';
import { CacheKey } from "../types/cache-keys";
import {
  useAddItemMutation,
  useFetchItems,
  useDeleteItemMutation,
  useUpdateItemMutation,
  validId,
} from "./base-hooks";
import { Tag } from "../types/tag";
import { PartialWithId } from "../types/base-types";


type UseTagManagementProps = {
  parentId: number | null;
  parentType: CacheKey;
  enabled?: boolean;
};

type QueryConfig = Partial<{
  [K in CacheKey]: {
    parentField: string;
    joinEndpoint: string;
    joinCacheKey: CacheKey;
    additionalParentCacheKey: CacheKey;
  };
}>;

const queryConfig: QueryConfig = {
  [CacheKey.GtmSolution]: {
    parentField: "go_to_market",
    joinEndpoint: "db/go-to-market-to-tag",
    joinCacheKey: CacheKey.GtmSolutionToTag,
    additionalParentCacheKey: CacheKey.GtmSolution,
  },
  [CacheKey.JourneyMap]: {
    parentField: "journey_map",
    joinEndpoint: "db/journey-map-to-tag",
    joinCacheKey: CacheKey.JourneyMapToTag,
    additionalParentCacheKey: CacheKey.JourneyMap,
  },
  [CacheKey.Product]: {
    parentField: "product",
    joinEndpoint: "db/product-to-tag",
    joinCacheKey: CacheKey.ProductToTag,
    additionalParentCacheKey: CacheKey.Product,
  },
  [CacheKey.ProductFeature]: {
    parentField: "product_feature",
    joinEndpoint: "db/product-feature-to-tag",
    joinCacheKey: CacheKey.ProductFeatureToTag,
    additionalParentCacheKey: CacheKey.ProductFeature,
  },
  [CacheKey.ProductComponent]: {
    parentField: "product_component",
    joinEndpoint: "db/product-component-to-tag",
    joinCacheKey: CacheKey.ProductComponentToTag,
    additionalParentCacheKey: CacheKey.ProductComponent,
  },
  [CacheKey.ProductUseCase]: {
    parentField: "product_use_case",
    joinEndpoint: "db/product-use-case-to-tag",
    joinCacheKey: CacheKey.ProductUseCaseToTag,
    additionalParentCacheKey: CacheKey.ProductUseCase,
  },
  [CacheKey.GtmSolutionUseCaseComponent]: {
    parentField: "security_use_case_component",
    joinEndpoint: "db/security-use-case-component-to-tag",
    joinCacheKey: CacheKey.GtmSolutionUseCaseComponentToTag,
    additionalParentCacheKey: CacheKey.GtmSolutionUseCaseComponent,
  },
  [CacheKey.GtmSolutionUseCase]: {
    parentField: "security_use_case",
    joinEndpoint: "db/security-use-case-to-tag",
    joinCacheKey: CacheKey.GtmSolutionUseCaseToTag,
    additionalParentCacheKey: CacheKey.GtmSolutionUseCase,
  },
  [CacheKey.GtmSolutionComponent]: {
    parentField: "solution_component",
    joinEndpoint: "db/solution-component-to-tag",
    joinCacheKey: CacheKey.GtmSolutionComponentToTag,
    additionalParentCacheKey: CacheKey.GtmSolutionComponent,
  },
};

type ParentFieldType = typeof queryConfig[keyof typeof queryConfig]['parentField'];
type JoinedTag = {
  id: number;
  tag: Tag['id'];
  [key: ParentFieldType]: number;
}
interface AttachedTag extends Tag {
  joinId: JoinedTag['id'];
  parentChain: string[];
}

type AttachedTagGroups = Record<Tag['key'] | "Uncategorized", AttachedTag[]>
type AvailableTagGroups = Record<Tag['key'] | "Uncategorized", Tag[]> 


export const useFetchTags = (options) => useFetchItems<Tag[]>(
  "db/tag",
  CacheKey.Tag,
  { size: 1000, detailed: true },
  options
);

export const useAddTagMutation = () => useAddItemMutation<Tag>('db/tag', () => [CacheKey.Tag]);
export const useUpdateTagMutation = () => useUpdateItemMutation<Tag>('db/tag', (payload) => [CacheKey.Tag, payload.id]);

const useFetchJoinedTags = (
  joinEndpoint: string,
  joinCacheKey: CacheKey,
  additionalParentCacheKey: CacheKey,
  parentField: string,
  parentId: number,
  enabled: boolean
) => {
  const validatedId = validId(parentId);
  return useFetchItems<JoinedTag[]>(
    joinEndpoint,
    () => [
      joinCacheKey,
      additionalParentCacheKey,
      ...(validatedId ? [String(parentId)] : [])
    ],
    validatedId ? { [parentField]: parentId, detailed: true } : {},
    { enabled: enabled && validatedId }
  );
};

const useAddJoinedTagMutation = (
  joinEndpoint: string,
  joinCacheKey: CacheKey,
  additionalParentCacheKey: CacheKey,
  parentId: number
) => {
  return useAddItemMutation<JoinedTag>(joinEndpoint, () => [
    joinCacheKey,
    additionalParentCacheKey,
    String(parentId),
  ]);
};

const useDeleteJoinedTagMutation = (
  joinEndpoint: string,
  joinCacheKey: CacheKey,
  additionalParentCacheKey: CacheKey,
  parentId: number
) => {
  return useDeleteItemMutation<PartialWithId<JoinedTag>>(joinEndpoint, (payload) => [
    joinCacheKey,
    additionalParentCacheKey,
    String(parentId),
    String(payload.id),
  ]);
};

export function useTagManagement({
  parentId,
  parentType,
  enabled = true,
}: UseTagManagementProps) {
  const queryClient = useQueryClient();

  const {
    parentField,
    joinEndpoint,
    joinCacheKey,
    additionalParentCacheKey,
  } = queryConfig[parentType];

  const [tagEditorOpen, setTagEditorOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);

  // Fetch/Mutations
  const { data: tags, isLoading: tagsLoading } = useFetchTags({ enabled: enabled });
  const { data: joinedTags, isLoading: joinedTagsLoading } = useFetchJoinedTags(joinEndpoint, joinCacheKey, additionalParentCacheKey, parentField, parentId, enabled);
  const { mutateAsync: attachTag } = useAddJoinedTagMutation(joinEndpoint, joinCacheKey, additionalParentCacheKey, parentId);
  const { mutateAsync: detachTag } = useDeleteJoinedTagMutation(joinEndpoint, joinCacheKey, additionalParentCacheKey, parentId);

  // Build tag lookup map
  const tagsById: Map<Tag['id'], Tag> = useMemo(
    () => new Map((tags || []).map((t: Tag) => [t.id, t])),
    [tags]
  );

  // Build parent chain for hierarchy display
  const getParentChain = (tagId: number): string[] => {
    const chain: string[] = [];
    let current = tagsById.get(tagId);

    while (current?.parent) {
      const parent = tagsById.get(current.parent);
      if (!parent) break;
      chain.unshift(`${parent.key}:${parent.value}`);
      current = parent;
    }

    return chain;
  };

  // Get attached tags with hierarchy info
  const attachedTags: AttachedTag[] = useMemo(() => {
    return (joinedTags || [])
      .map((jr: JoinedTag) => {
        const tag = tagsById.get(jr.tag);
        if (!tag) return null;
        return {
          joinId: jr.id,
          ...tag,
          parentChain: getParentChain(jr.tag),
        } as AttachedTag;
      })
      .filter(Boolean);
  // not adding getParentChain to dependencies to avoid unnecessary recalculation
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinedTags, tagsById]);

  // Group tags by type
  const attachedTagsByKey: AttachedTagGroups = useMemo(() => {
    const grouped: AttachedTagGroups = {};
    attachedTags.forEach((tag: AttachedTag) => {
      const groupKey = tag.key || "Uncategorized";
      if (!grouped[groupKey]) grouped[groupKey] = [];
      grouped[groupKey].push(tag);
    });
    return grouped;
  }, [attachedTags]);

  // Filter available tags (exclude already attached)
  const availableTags: Tag[] = useMemo(() => {
    const attachedIds = new Set(attachedTags.map((t: AttachedTag) => t.id));
    return (tags || []).filter((t: Tag) => !attachedIds.has(t.id));
  }, [tags, attachedTags]);

  // Group available tags by type for dropdown
  const availableTagsByKey: AvailableTagGroups = useMemo(() => {
    const grouped: AvailableTagGroups = {};
    availableTags.forEach((tag: Tag) => {
      const groupKey = tag.key || "Uncategorized";
      if (!grouped[groupKey]) grouped[groupKey] = [];
      grouped[groupKey].push(tag);
    });
    return grouped;
  }, [availableTags]);

  // Handlers
  const handleCreateTag = () => {
    setEditingTag(null);
    setTagEditorOpen(true);
  };

  const handleEditOrViewTag = (tagId: Tag['id']) => {
    if (!tagId || isNaN(tagId)) {
      console.error('Invalid tagId:', tagId);
      return;
    }
    setEditingTag(tagsById.get(tagId) || null);
    setTagEditorOpen(true);
  };

  const handleTagSaved = async (newTagId?: Tag['id']) => {
    await queryClient.refetchQueries({ queryKey: [CacheKey.Tag] });

    // If creating a new tag (has newTagId and NOT editing) and we have a parentId, auto-attach
    if (newTagId && parentId !== null && parentId !== undefined && !editingTag) {
      try {
        await attachTag({
          [parentField]: parentId,
          tag: newTagId,
        });
      } catch (error) {
        console.error("Failed to auto-attach tag:", error); 
      }
    }

    setTagEditorOpen(false);
    setEditingTag(null);
  };

  const handleAttachTag = async () => {
    if (parentId === null || parentId === undefined || !selectedTag?.id) return;

    await attachTag({
      [parentField]: parentId,
      tag: selectedTag.id,
    });

    setSelectedTag(null);
  };

  const handleDetachTag = async (joinId: number) => {
    await detachTag({ id: joinId });
  };

  return {
    // State
    tagEditorOpen,
    editingTag,
    selectedTag,
    setSelectedTag,

    // Data
    allTags: tags ?? [],
    attachedTags,
    attachedTagsByKey,
    availableTags,
    availableTagsByKey,

    // Handlers
    handleCreateTag,
    handleEditOrViewTag,
    handleTagSaved,
    handleAttachTag,
    handleDetachTag,
    setTagEditorOpen,
    setEditingTag,
    getParentChain,

    // Utilities
    canManage: parentId !== null && parentId !== undefined,
    isLoading: tagsLoading || joinedTagsLoading,
  };
}